import React, { useEffect, useMemo, useState } from "react";
import useEstadoSalvamento from "../Utils/useEstadoSalvamento";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { addDoc, collection, doc, getDocs, orderBy, query, updateDoc } from "firebase/firestore";
import Navbar from "../Components/Navbar/navbar";
import HistoricoCadastro from "../Components/HistoricoCadastro/HistoricoCadastro";
import { db } from "../Config/firebase";
import RelatorioGuiaDosagemIndividualPrint from "./RelatorioGuiaDosagemIndividualPrint";
import "./CadastroGuiaDosagem.css";
import { consultaTenantAtual , aplicarTenantAtual} from "../Utils/tenant";

const materiaisIniciais = [
  { nome: "CIMENTO", fornecedor: "", tipo: "cimento", peso: "", unidade: "kg" },
  { nome: "BRITA 1", fornecedor: "", tipo: "agregado", peso: "", unidade: "kg" },
  { nome: "BRITA 0", fornecedor: "", tipo: "agregado", peso: "", unidade: "kg" },
  { nome: "AREIA NATURAL", fornecedor: "", tipo: "agregado", peso: "", unidade: "kg" },
  { nome: "AREIA ARTIFICIAL", fornecedor: "", tipo: "agregado", peso: "", unidade: "kg" },
  { nome: "ADITIVO 1", fornecedor: "", tipo: "aditivo", peso: "", unidade: "l" },
  { nome: "ADITIVO 2", fornecedor: "", tipo: "aditivo", peso: "", unidade: "l" },
  { nome: "ÁGUA", fornecedor: "", tipo: "agua", peso: "", unidade: "l" },
];

const estadoInicial = {
  data: new Date().toISOString().slice(0, 10),
  clienteId: "",
  clienteNome: "",
  obraId: "",
  obraNome: "",
  traco: "",
  fck: "",
  fatorAc: "",
  slump: "",
  toleranciaSlump: "",
  tipoGuia: "acumulada",
  materiais: materiaisIniciais,
};

function normalizarTexto(valor) {
  return String(valor || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
}

function numero(valor) {
  if (valor === null || valor === undefined || valor === "") return 0;
  const convertido = Number(String(valor).replace(/\./g, "").replace(",", "."));
  return Number.isFinite(convertido) ? convertido : 0;
}

function numeroBR(valor, casas = 0) {
  return (Number(valor) || 0).toLocaleString("pt-BR", {
    minimumFractionDigits: casas,
    maximumFractionDigits: casas,
  });
}

function formatarDataBR(data) {
  if (!data) return "-";
  if (typeof data === "string" && data.includes("-")) {
    const [ano, mes, dia] = data.split("-");
    return `${dia}/${mes}/${ano}`;
  }
  if (data?.toDate) return data.toDate().toLocaleDateString("pt-BR");
  return String(data);
}


function volumesPadrao() {
  return Array.from({ length: 15 }, (_, index) => 1 + index * 0.5);
}

function colunasPesagem(guia) {
  const materiais = (guia.materiais || []).filter((material) => String(material.nome || "").trim());

  if (guia.tipoGuia === "individual") {
    return materiais.map((material, index) => ({
      id: `${material.nome}-${index}`,
      titulo: material.nome,
      unidade: material.unidade || (material.tipo === "aditivo" ? "l" : "kg"),
      valor: (volume) => numero(material.peso) * volume,
    }));
  }

  let acumuladoAgregados = 0;
  return materiais.map((material, index) => {
    const peso = numero(material.peso);
    const tipo = material.tipo || "agregado";
    const acumulado = tipo === "agregado" ? (acumuladoAgregados += peso) : peso;

    return {
      id: `${material.nome}-${index}`,
      titulo: material.nome,
      unidade: material.unidade || (tipo === "aditivo" ? "l" : "kg"),
      valor: (volume) => acumulado * volume,
      acumulada: tipo === "agregado",
    };
  });
}



function ehTipo(material, tipo) {
  return String(material?.tipo || "").toLowerCase() === tipo;
}

function nomeNormalizado(valor) {
  return normalizarTexto(valor).replace(/[^a-z0-9]+/g, " ").trim();
}

function ehAreia(material) {
  const nome = nomeNormalizado(material?.nome);
  return nome.includes("areia") && !nome.includes("po");
}

function ehPo(material) {
  const nome = nomeNormalizado(material?.nome);
  return nome === "po" || nome.includes(" po ") || nome.includes("po de pedra");
}

function ehAgua(material) {
  return ehTipo(material, "agua") || nomeNormalizado(material?.nome).includes("agua");
}

function ehAditivo(material) {
  return ehTipo(material, "aditivo");
}

function ehAgregadoConstante(material) {
  const nome = nomeNormalizado(material?.nome);
  return (
    !ehAreia(material) &&
    !ehPo(material) &&
    !ehAgua(material) &&
    !ehAditivo(material) &&
    !ehTipo(material, "cimento") &&
    !ehTipo(material, "adicao") &&
    (ehTipo(material, "agregado") || nome.includes("brita") || nome.includes("pedrisco"))
  );
}

function unidadePorTipo(tipo) {
  const tipoNormalizado = normalizarTexto(tipo);
  if (tipoNormalizado === "aditivo" || tipoNormalizado === "agua") return "l";
  return "kg";
}

function unidadeMaterial(material) {
  return unidadePorTipo(material?.tipo);
}

function guiaEhIndividual(guia) {
  return ["individual", "pesoindividual", "pesosindividuais"].includes(
    normalizarTexto(guia?.tipoGuia).replace(/\s+/g, "")
  );
}

function tituloTabelaPesagem(guia) {
  return guiaEhIndividual(guia)
    ? "TABELA DE PESAGEM NA CENTRAL DE CONCRETO (AGREGADOS COM PESOS INDIVIDUAIS, UM SILO PARA CADA AGREGADO)"
    : "TABELA DE PESAGEM NA CENTRAL DE CONCRETO (AGREGADOS COM PESOS ACUMULADOS, BRITA + AREIA)";
}

function colunasConstantesGuia(guia) {
  const materiais = (guia.materiais || []).filter((material) => String(material.nome || "").trim());
  let acumuladoAgregados = 0;

  return materiais
    .filter((material) => !ehAreia(material) && !ehPo(material) && !ehAgua(material))
    .map((material, index) => {
      const peso = numero(material.peso);
      const agregadoConstante = ehAgregadoConstante(material);
      const valorBase =
        !guiaEhIndividual(guia) && agregadoConstante
          ? (acumuladoAgregados += peso)
          : peso;

      return {
        id: `${material.nome}-${index}`,
        grupo: ehAditivo(material) ? "aditivo" : "base",
        titulo: material.nome || "-",
        unidade: unidadeMaterial(material),
        valor: (volume) => numeroBR(valorBase * volume, unidadeMaterial(material) === "l" ? 1 : 0),
      };
    });
}

function linhasPesagemGuia(guia) {
  const materiais = guia.materiais || [];
  const areia = materiais.find(ehAreia);
  const po = materiais.find(ehPo);
  const agua = materiais.find(ehAgua);
  const pesoGraudos = materiais
    .filter(ehAgregadoConstante)
    .reduce((total, material) => total + numero(material.peso), 0);
  const pesoAreia = numero(areia?.peso);
  const pesoPo = numero(po?.peso);
  const pesoAgua = numero(agua?.peso);
  const umidades = [2, 3, 4, 5, 6, 7, 8];

  return volumesPadrao().map((volume) => ({
    volume,
    umidades: umidades.map((umidade) => {
      const totalGraudos = guiaEhIndividual(guia) ? 0 : pesoGraudos * volume;
      const pesoAreiaUmida = pesoAreia * volume * (1 + umidade / 100);
      const pesoPoUmido = pesoPo * volume * (1 + umidade / 100);
      const correcaoAgua = (pesoAreia + pesoPo) * volume * (umidade / 100);
      return {
        umidade,
        areia: pesoAreia ? totalGraudos + pesoAreiaUmida : 0,
        po: pesoPo ? totalGraudos + pesoAreiaUmida + pesoPoUmido : 0,
        agua: Math.max(0, pesoAgua * volume - correcaoAgua),
      };
    }),
  }));
}

function itensResumoGuiaManual(guia) {
  return (guia.materiais || [])
    .filter((material) => String(material.nome || "").trim())
    .map((material) => ({
      titulo: material.nome || "-",
      valor: `${numeroBR(numero(material.peso), unidadeMaterial(material) === "l" ? 1 : 0)} ${unidadeMaterial(material)}/m³`,
      descricao: material.fornecedor || "-",
      fornecedor: "",
    }));
}


export default function CadastroGuiaDosagem() {
  const { salvandoCadastro, executarSalvamento } = useEstadoSalvamento();
  const [clientes, setClientes] = useState([]);
  const [obras, setObras] = useState([]);
  const [guias, setGuias] = useState([]);
  const [carregandoLista, setCarregandoLista] = useState(true);
  const [filtro, setFiltro] = useState("");
  const [openDialog, setOpenDialog] = useState(false);
  const [guiaEditando, setGuiaEditando] = useState(null);
  const [form, setForm] = useState(estadoInicial);
  const [guiaPrint, setGuiaPrint] = useState(null);

  useEffect(() => {
    carregarDados();
  }, []);

  async function carregarDados() {
    const [clientesSnap, obrasSnap, guiasSnap] = await Promise.all([
      getDocs(consultaTenantAtual(query(collection(db, "clientes"), orderBy("nome")))),
      getDocs(consultaTenantAtual(query(collection(db, "obras"), orderBy("nome")))),
      getDocs(consultaTenantAtual(query(collection(db, "tblGuiasDosagem"), orderBy("criadoEm", "desc")))),
    ]);

    setClientes(clientesSnap.docs.map((item) => ({ id: item.id, ...item.data() })));
    setObras(obrasSnap.docs.map((item) => ({ id: item.id, ...item.data() })));
    setGuias(guiasSnap.docs.map((item) => ({ id: item.id, ...item.data() })));
    setCarregandoLista(false);
  }

  const clienteSelecionado = clientes.find((cliente) => cliente.id === form.clienteId) || null;
  const obraSelecionada = obras.find((obra) => obra.id === form.obraId) || null;
  const obrasDoCliente = obras.filter((obra) => !form.clienteId || obra.clienteId === form.clienteId);

  const listaFiltrada = guias.filter((guia) => {
    const termo = normalizarTexto(filtro);
    if (!termo) return true;
    return [guia.clienteNome, guia.obraNome, guia.traco, guia.tipoGuia].some((valor) =>
      normalizarTexto(valor).includes(termo)
    );
  });

  function novaGuia() {
    setGuiaEditando(null);
    setForm({
      ...estadoInicial,
      data: new Date().toISOString().slice(0, 10),
      materiais: materiaisIniciais.map((item) => ({ ...item })),
    });
    setOpenDialog(true);
  }

  function editarGuia(guia) {
    setGuiaEditando(guia);
    setForm({
      ...estadoInicial,
      ...guia,
      materiais: Array.isArray(guia.materiais) && guia.materiais.length
        ? guia.materiais
        : materiaisIniciais.map((item) => ({ ...item })),
    });
    setOpenDialog(true);
  }

  function atualizarMaterial(index, campo, valor) {
    setForm((atual) => ({
      ...atual,
      materiais: atual.materiais.map((material, itemIndex) =>
        itemIndex === index
          ? {
              ...material,
              [campo]: valor,
              ...(campo === "tipo" ? { unidade: unidadePorTipo(valor) } : {}),
            }
          : material
      ),
    }));
  }

  function adicionarMaterial() {
    setForm((atual) => ({
      ...atual,
      materiais: [...atual.materiais, { nome: "", fornecedor: "", tipo: "agregado", peso: "", unidade: unidadePorTipo("agregado") }],
    }));
  }

  async function salvarGuia(abrirRelatorio = false) {
    if (!form.clienteId) {
      alert("Selecione o cliente.");
      return;
    }

    if (!form.obraId) {
      alert("Selecione a obra.");
      return;
    }

    const materiaisValidos = (form.materiais || []).filter((material) =>
      String(material.nome || "").trim() && numero(material.peso) > 0
    ).map((material) => ({
      ...material,
      unidade: unidadeMaterial(material),
    }));

    if (!materiaisValidos.length) {
      alert("Informe pelo menos um material com peso.");
      return;
    }

    const dados = {
      ...form,
      clienteNome: clienteSelecionado?.nome || form.clienteNome || "",
      obraNome: obraSelecionada?.nome || form.obraNome || "",
      materiais: materiaisValidos,
      atualizadoEm: new Date(),
    };

    let salvo;
    if (guiaEditando?.id) {
      await updateDoc(doc(db, "tblGuiasDosagem", guiaEditando.id), dados);
      salvo = { ...guiaEditando, ...dados };
    } else {
      const ref = await addDoc(collection(db, "tblGuiasDosagem"), aplicarTenantAtual({
        ...dados,
        criadoEm: new Date(),
      }));
      salvo = { id: ref.id, ...dados };
    }

    setOpenDialog(false);
    setGuiaEditando(null);
    await carregarDados();

    if (abrirRelatorio) setGuiaPrint(salvo);
  }

  if (guiaPrint) {
  return (
    <RelatorioGuiaDosagemIndividualPrint
      guia={guiaPrint}
      onVoltar={() => setGuiaPrint(null)}
    />
  );
}

  return (
    <>
      <Navbar />

      <Box sx={{ p: { xs: 2, md: 4 } }}>
        <Paper className="cabecalho-lista-cadastro" elevation={0}>
        <Typography variant="h6" fontWeight={700}>Guias de Dosagem</Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>Cadastro simples de materiais e pesos para guia de dosagem</Typography>
        <Stack direction={{ xs: "column", md: "row" }} spacing={2} mb={3}>
          <Button variant="contained" startIcon={<AddIcon />} onClick={novaGuia}>Nova Guia</Button>
          <TextField
            size="small"
            label="Pesquisar"
            value={filtro}
            onChange={(event) => setFiltro(event.target.value)}
            sx={{ flex: 1 }}
          />
          <TextField size="small" label="Qtd" value={listaFiltrada.length} InputProps={{ readOnly: true }} sx={{ width: 110 }} />
        </Stack>
        </Paper>

        <Paper sx={{ borderRadius: "8px", overflow: "auto", border: "1px solid #e2e8f0", boxShadow: "none" }}>
          <Table className="lista-cadastro-padrao" size="small" sx={{ minWidth: 900 }}>
            <TableHead sx={{ backgroundColor: "#f8fafc" }}>
              <TableRow>
                <TableCell>Cliente / Obra</TableCell>
                <TableCell>Traço</TableCell>
                <TableCell>Tipo</TableCell>
                <TableCell>Data</TableCell>
                <TableCell align="center">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {listaFiltrada.map((item) => (
                <TableRow key={item.id} hover>
                  <TableCell>
                    <div className="lista-dado-principal">{item.clienteNome || "-"}</div>
                    <div className="lista-dado-secundario">Obra: {item.obraNome || "-"}</div>
                  </TableCell>
                  <TableCell className="lista-dado-destaque">{item.traco || "-"}</TableCell>
                  <TableCell className="lista-dado-secundario">{guiaEhIndividual(item) ? "Individual" : "Acumulada"}</TableCell>
                  <TableCell className="lista-dado-secundario">{formatarDataBR(item.data)}</TableCell>
                  <TableCell align="center">
                    <Tooltip title="Visualizar relatório">
                      <IconButton size="small" onClick={() => setGuiaPrint(item)}>
                        <VisibilityIcon color="primary" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Editar">
                      <IconButton size="small" onClick={() => editarGuia(item)}>
                        <EditIcon sx={{ color: "#0288d1" }} />
                      </IconButton>
                    </Tooltip>
                    <HistoricoCadastro colecao="tblGuiasDosagem" documentoId={item.id} registro={item} />
                  </TableCell>
                </TableRow>
              ))}

              {carregandoLista && <TableRow><TableCell colSpan={5}><Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 1.5, py: 4 }}><CircularProgress /><Typography color="text.secondary">Carregando registros...</Typography></Box></TableCell></TableRow>}
              {!carregandoLista && listaFiltrada.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center">Nenhuma guia cadastrada.</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Paper>
      </Box>

      <Dialog open={openDialog} onClose={() => !salvandoCadastro && setOpenDialog(false)} fullWidth maxWidth="lg">
        <DialogTitle>{guiaEditando ? "Editar Guia de Dosagem" : "Nova Guia de Dosagem"}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} mt={1}>
            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
              <TextField label="Data" type="date" InputLabelProps={{ shrink: true }} value={form.data} onChange={(event) => setForm({ ...form, data: event.target.value })} sx={{ width: { md: 190 } }} />
              <Autocomplete
                options={clientes}
                getOptionLabel={(option) => option?.nome || ""}
                value={clienteSelecionado}
                onChange={(event, value) => setForm({ ...form, clienteId: value?.id || "", obraId: "" })}
                renderInput={(params) => <TextField {...params} label="Cliente" />}
                fullWidth
              />
              <Autocomplete
                options={obrasDoCliente}
                getOptionLabel={(option) => option?.nome || ""}
                value={obraSelecionada}
                onChange={(event, value) => setForm({ ...form, obraId: value?.id || "" })}
                renderInput={(params) => <TextField {...params} label="Obra" />}
                fullWidth
              />
            </Stack>

            <Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ width: "100%" }}>
              <TextField label="Traço" value={form.traco} onChange={(event) => setForm({ ...form, traco: event.target.value })} sx={{ flex: 1, minWidth: { md: 180 } }} />
              <TextField label="FCK (MPa)" value={form.fck} onChange={(event) => setForm({ ...form, fck: event.target.value })} sx={{ width: { md: 120 } }} />
              <TextField label="Fator A/C" value={form.fatorAc} onChange={(event) => setForm({ ...form, fatorAc: event.target.value })} sx={{ width: { md: 120 } }} />
              <TextField label="Slump" value={form.slump} onChange={(event) => setForm({ ...form, slump: event.target.value })} sx={{ width: { md: 110 } }} />
              <TextField label="Tolerância" value={form.toleranciaSlump || ""} onChange={(event) => setForm({ ...form, toleranciaSlump: event.target.value })} sx={{ width: { md: 120 } }} />
              <FormControl sx={{ width: { md: 210 }, flexShrink: 0 }}>
                <InputLabel>Tipo de guia</InputLabel>
                <Select label="Tipo de guia" value={form.tipoGuia} onChange={(event) => setForm({ ...form, tipoGuia: event.target.value })}>
                  <MenuItem value="acumulada">Acumulada</MenuItem>
                  <MenuItem value="individual">Pesos individuais por silo</MenuItem>
                </Select>
              </FormControl>
            </Stack>

            <Paper variant="outlined" sx={{ overflow: "auto" }}>
              <Table size="small" className="guia-form-tabela">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ minWidth: 180 }}>Material</TableCell>
                    <TableCell sx={{ minWidth: 320 }}>Fornecedor</TableCell>
                    <TableCell sx={{ minWidth: 150 }}>Tipo</TableCell>
                    <TableCell sx={{ width: 110 }}>Peso base</TableCell>
                    <TableCell sx={{ width: 80 }}>Unid.</TableCell>
                    <TableCell align="center">Ações</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {form.materiais.map((material, index) => (
                    <TableRow key={index}>
                      <TableCell><TextField size="small" value={material.nome} onChange={(event) => atualizarMaterial(index, "nome", event.target.value)} autoComplete="off" inputProps={{ autoComplete: "off" }} fullWidth /></TableCell>
                      <TableCell><TextField size="small" value={material.fornecedor || ""} onChange={(event) => atualizarMaterial(index, "fornecedor", event.target.value)} autoComplete="off" inputProps={{ autoComplete: "off" }} fullWidth /></TableCell>
                      <TableCell>
                        <Select size="small" value={material.tipo || "agregado"} onChange={(event) => atualizarMaterial(index, "tipo", event.target.value)} fullWidth>
                          <MenuItem value="cimento">Cimento</MenuItem>
                          <MenuItem value="agregado">Agregado</MenuItem>
                          <MenuItem value="adicao">Adição</MenuItem>
                          <MenuItem value="aditivo">Aditivo</MenuItem>
                          <MenuItem value="agua">Água</MenuItem>
                        </Select>
                      </TableCell>
                      <TableCell><TextField size="small" value={material.peso} onChange={(event) => atualizarMaterial(index, "peso", event.target.value)} autoComplete="off" inputProps={{ autoComplete: "off", style: { textAlign: "center" } }} sx={{ width: 95 }} /></TableCell>
                      <TableCell align="center">
                        <Typography fontWeight={700}>{unidadeMaterial(material)}</Typography>
                      </TableCell>
                      <TableCell align="center">
                        <IconButton color="error" onClick={() => setForm((atual) => ({ ...atual, materiais: atual.materiais.filter((_, itemIndex) => itemIndex !== index) }))}>
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>

            <Button variant="outlined" startIcon={<AddIcon />} onClick={adicionarMaterial} sx={{ alignSelf: "flex-start" }}>
              Adicionar material
            </Button>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)} disabled={salvandoCadastro}>Cancelar</Button>
          <Button variant="outlined" onClick={() => executarSalvamento(() => salvarGuia(false))} disabled={salvandoCadastro}>
            {salvandoCadastro ? "Salvando..." : "Salvar"}
          </Button>
          <Button variant="contained" onClick={() => executarSalvamento(() => salvarGuia(true))} disabled={salvandoCadastro}>
            {salvandoCadastro ? "Salvando..." : "Salvar e abrir relatório"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
